/** @file ScopeChoices: the New puppet modal's directions, head pitch, camera and profile rows. */
import type { ReactElement } from 'react';
import type { TemplateEntry } from '../store/editorApi';
import type { Choice } from '../ui-kit/ChoiceRow';
import { ChoiceRow } from '../ui-kit/ChoiceRow';
import { DirectionGlyph } from './DirectionGlyph';
import type { CameraChoice, DirectionsChoice, NewPuppetDraft, PitchChoice, ProfileChoice } from './newPuppetTypes';

type Item<T> = { value: T; caption: string; label: string };

const DIRECTIONS: DirectionsChoice[] = [1, 2, 4, 8, 16];
const PITCH: Item<PitchChoice>[] = [
  { value: 'none', caption: 'none', label: 'Head pitch none (one head tile, level view)' },
  { value: 'basic', caption: 'basic', label: 'Head pitch basic (level, up, down)' },
  { value: 'full', caption: 'full', label: 'Head pitch full (every band)' },
];
const CAMERA: Item<CameraChoice>[] = [
  { value: 'iso', caption: 'iso', label: 'Camera iso (looking down)' },
  { value: 'side', caption: 'side', label: 'Camera side (a plain 2D side view)' },
];
const PROFILE: Item<ProfileChoice>[] = [
  { value: 'pixel16', caption: 'pixel16', label: 'Profile pixel16 (16 px per tile, pixel art)' },
  { value: 'hd', caption: 'hd', label: 'Profile hd (smooth, high resolution)' },
];

/** Whether `value` is allowed by the template's declared `scopes` list (no list = everything is). */
const allows = (list: (string | number)[] | undefined, value: string | number): boolean => list === undefined || list.includes(value);

/** The choices of one row, disabled where the template's `scopes` leave them out. */
const rowOf = <T extends string | number>(items: Item<T>[], list: (string | number)[] | undefined): Choice<T>[] =>
  items.map((c) => ({ ...c, disabled: !allows(list, c.value) }));

/** The draft, the chosen template (its `scopes` limit the rows) and the callback that patches the draft. */
export type ScopeChoicesProps = { draft: NewPuppetDraft; template: TemplateEntry | null; onChange: (patch: Partial<NewPuppetDraft>) => void };

/** Four rows, nothing preselected; directions show a pictogram of the spokes drawn. */
export function ScopeChoices({ draft, template, onChange }: ScopeChoicesProps): ReactElement {
  const scopes = template?.scopes;
  const directions = DIRECTIONS.map((n): Choice<DirectionsChoice> => ({
    value: n, label: `${String(n)} direction${n === 1 ? '' : 's'}`, caption: String(n), icon: <DirectionGlyph n={n} />,
    disabled: !allows(scopes?.directions, n),
  }));
  return (
    <>
      <ChoiceRow label="Directions" choices={directions} value={draft.directions} onChange={(directions) => { onChange({ directions }); }} />
      <ChoiceRow label="Head pitch" choices={rowOf(PITCH, scopes?.pitch)} value={draft.pitch} onChange={(pitch) => { onChange({ pitch }); }} />
      <ChoiceRow label="Camera" choices={rowOf(CAMERA, scopes?.cameras)} value={draft.camera} onChange={(camera) => { onChange({ camera }); }} />
      <ChoiceRow label="Profile" choices={rowOf(PROFILE, undefined)} value={draft.profile} onChange={(profile) => { onChange({ profile }); }} />
    </>
  );
}
